import type { PieceRenderObject } from 'react-chessboard';
import { pieceTypes } from '../../design/assets';
/** Locally stored Chess.com PNGs; provenance and hashes live beside the assets. */
export const pieceImage = (piece: string) => `/vendor/chesscom/pieces/${piece.toLowerCase()}.png`;
export const boardImage = '/vendor/chesscom/board.png';
const boardUrls = [
  boardImage,
  ...['w', 'b'].flatMap((c) => pieceTypes.map((t) => pieceImage(c + t))),
];

/**
 * In-memory copies of the board and piece images (R5 flicker fix).
 *
 * The board library creates a new <img> for the piece that moves, and for every
 * piece on a new puzzle. When the offline service worker controls the page, Safari
 * skips its memory cache and fetches each new <img> through the worker, so pieces
 * blink while they reload. Drawing from blob: URLs loaded once keeps every image
 * in memory: nothing is fetched or re-downloaded while playing.
 */
const memory = new Map<string, string>();
/** Decoded images stay referenced so the browser keeps their bitmaps. */
const decoded = new Map<string, HTMLImageElement>();
const inflight = new Map<string, Promise<void>>();
export const imageSrc = (url: string) => memory.get(url) ?? url;

/**
 * Fetch and decode the board and all twelve pieces in the background while the
 * player is on home, keeping them in memory for the rest of the visit. Best
 * effort: a failure only means the plain URL is used.
 */
export function warmBoardImages(
  make: () => HTMLImageElement = () => new Image(),
  load: (url: string) => Promise<string | undefined> = async (url) => {
    const response = await fetch(url, { signal: AbortSignal.timeout(5000) });
    if (!response.ok) return undefined;
    return URL.createObjectURL(await response.blob());
  },
) {
  return boardUrls.map((url) => {
    if (memory.has(url)) return Promise.resolve();
    const pending = inflight.get(url);
    if (pending) return pending;
    const work = (async () => {
      let local: string | undefined;
      try {
        local = await load(url);
        const image = make();
        image.decoding = 'async';
        image.src = local ?? url;
        await image.decode?.();
        // A failed decode must never poison later piece renders with a broken blob.
        if (local) memory.set(url, local);
        decoded.set(url, image);
      } catch {
        if (local?.startsWith('blob:')) URL.revokeObjectURL(local);
        /* The plain URL still works; another warm-up can retry. */
      }
    })().finally(() => inflight.delete(url));
    inflight.set(url, work);
    return work;
  });
}

export const pieces: PieceRenderObject = Object.fromEntries(
  ['w', 'b'].flatMap((color) =>
    pieceTypes.map((type) => [
      color + type,
      () => (
        <img
          className="chess-piece"
          src={imageSrc(pieceImage(color + type))}
          // Which piece this is, readable even while src is an in-memory blob: URL.
          data-src={pieceImage(color + type)}
          alt=""
          aria-hidden="true"
          draggable={false}
          // A new piece image paints decoded, never blank for a frame.
          decoding="sync"
          width={150}
          height={150}
        />
      ),
    ]),
  ),
);
